import { ContentSpinner } from '@taxbit-private/cosmic'
import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AccountingTab } from 'pages/accounting/helpers/types'
import { ContactsTab } from 'pages/contacts/helpers/types'
import { VoucherDetailsTab } from 'pages/vouchers/util/types'
import AccountingRouteGuard from 'routes/AccountingRouteGuard'
import { BasePath } from 'routes/paths'

import CosmicErrorBoundary from 'components/CosmicErrorBoundary'
import Permission from 'constants/Permissions'
import { LegalEntityProvider } from 'contexts/LegalEntityContext'
import { useUser } from 'contexts/UserContext'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { useCommandRegistry } from 'hooks/useCommandRegistry'
import useIsLegalEntityRootOrg from 'hooks/utils/useIsLegalEntityRootOrg'
import useIsZeusAdminOrg from 'hooks/utils/useIsZeusAdminOrg'
import useShowErpAccountsAvailableToast from 'store/notification-cosmic/useShowErpAccountsAvailableToast'
import { isZeusEnvironment } from 'utils/environment'

import Layout from './Layout'
import PrivateRoutes from './PrivateRoutes'
import { useIsTopLevelOrg } from './useIsTopLevelOrg'
import { ConfirmProvider } from '../../contexts/ConfirmContext'
import { BackendFiltersProvider } from '../../contexts/filters/BackendFiltersContext'
import { FiltersProvider } from '../../contexts/filters/FiltersContext'
import ReportFilterProvider from '../../contexts/ReportFilterContext'
import useRoutes from '../../routes/useRoutes'

const AccountingCosmicContent = lazy(() => import('pages/accounting/AccountingCosmic'))
const ChartOfAccounts = lazy(() => import('pages/accounting/chart-of-accounts/ChartOfAccounts'))
const DetailedMappings = lazy(() => import('pages/accounting/detailed-mappings/DetailedMappings'))
const Contacts = lazy(() => import('pages/contacts/Contacts'))
const SavedContactsContent = lazy(() => import('pages/contacts/saved-contacts/SavedContacts'))
const UnknownAddressesContacts = lazy(
  () => import('pages/contacts/unknown-addresses/UnknownAddresses')
)
const AccountingContent = lazy(() => import('pages/settings/accounting/AccountingSetting'))
const AuditLogExport = lazy(() => import('pages/settings/audit-log/AuditLogExport'))
const Categories = lazy(() => import('pages/settings/categories/Categories'))
const OverviewContent = lazy(() => import('pages/settings/overview/OverviewSetting'))
const PricingContent = lazy(() => import('pages/settings/pricing/PricingSetting'))
const SpamTokenSettingContent = lazy(() => import('pages/settings/spam-tokens/SpamTokenSetting'))
const StableCoinsContent = lazy(() => import('pages/settings/stable-coins/StableCoinsSetting'))
const TaxContent = lazy(() => import('pages/settings/tax/TaxSetting'))
const TeamContent = lazy(() => import('pages/settings/team/TeamSetting'))
const CustomFields = lazy(() => import('pages/super-admin/custom-fields/CustomFields'))
const EntitiesContent = lazy(() => import('pages/super-admin/entities/Entities'))
const ImposterAllowlistContent = lazy(
  () => import('pages/super-admin/imposter-allowlist/ImposterAllowlist')
)
const IngestionJobsContent = lazy(() => import('pages/super-admin/ingestion-jobs/IngestionJobs'))
const OrgControlsContent = lazy(() => import('pages/super-admin/org-controls/OrgControls'))
const OrganizationContent = lazy(() => import('pages/super-admin/organization/Organization'))
const RequestsContent = lazy(() => import('pages/super-admin/requests/Requests'))
const VoucherDetails = lazy(() => import('pages/vouchers/VoucherDetails'))
const VoucherOverview = lazy(() => import('pages/vouchers/VoucherOverview'))
const VoucherTransactions = lazy(() => import('pages/vouchers/VoucherTransactions'))

const AuthRequiredContent = () => {
  // todo: add a generic hook that will contain all hooks that will render global notifications
  // for better organization and more abstraction
  useShowErpAccountsAvailableToast()

  const { allRoutes } = useRoutes()

  const {
    shouldShowPricingSettings,
    isStableCoinsEnabled,
    showCategoriesSettings,
    isPublicSector,
    isAuditLogEnabled,
    enableCustomFields,
    isMultiBookEnabled,
  } = useFeatureFlags()

  const { isParentOrg } = useIsTopLevelOrg()

  const registerCommands = useCommandRegistry()
  const { user, hasPermission } = useUser()

  // Initialize commands when the app loads
  useEffect(() => {
    registerCommands()
  }, [registerCommands])

  const { isLegalEntityRootOrg, isLoading } = useIsLegalEntityRootOrg()

  const isZeusAdminOrg = useIsZeusAdminOrg()

  const shouldShowDashboard = !isPublicSector || !isParentOrg

  // Determine the best landing page based on user permissions
  const getDefaultLandingPath = () => {
    // The Zeus operational org hides Dashboard/Transactions/Reports/etc. from its
    // nav, so land it on Team instead of the (hidden) Dashboard. Checked explicitly
    // because the Zeus admin user is a superadmin, so hasPermission() is always true
    // and the permission-based branch below never triggers for them.
    if (isZeusAdminOrg) {
      return `${BasePath.Settings}/team`
    }

    // For Zeus admin users with limited permissions, prioritize settings pages
    const hasTeamPermission = hasPermission(Permission.SettingTeamRead)
    const hasEntityPermission = hasPermission(Permission.SettingEntityRead)
    const hasDashboardPermission = hasPermission(Permission.TransactionDashboardRead)
    const hasTransactionPermission = hasPermission(Permission.TransactionDataRead)

    // If user only has team/entity permissions but no transaction permissions,
    // land them on the most relevant settings page
    if (
      (hasTeamPermission || hasEntityPermission) &&
      !hasDashboardPermission &&
      !hasTransactionPermission
    ) {
      if (hasTeamPermission) {
        return `${BasePath.Settings}/team`
      }
      if (hasEntityPermission) {
        return `${BasePath.Settings}/entities`
      }
    }

    // Default logic for users with broader permissions
    return shouldShowDashboard ? BasePath.Dashboard : BasePath.Cases
  }

  const defaultLandingPath = getDefaultLandingPath()

  return isLoading ? (
    <></>
  ) : (
    <LegalEntityProvider>
      <ConfirmProvider>
        <FiltersProvider>
          <ReportFilterProvider>
            <BackendFiltersProvider>
              <Layout>
                <CosmicErrorBoundary trackingId="app-error-boundary">
                  <Suspense fallback={<ContentSpinner />}>
                    <Routes>
                      <Route element={<PrivateRoutes />}>
                        {allRoutes.map((route) => {
                          switch (route.path) {
                            case BasePath.Settings: {
                              return (
                                <Route path={route.path} key={route.name}>
                                  <Route index={true} element={<Navigate to="overview" />} />
                                  <Route path="overview" element={<OverviewContent />} />
                                  {shouldShowPricingSettings && (
                                    <Route path="pricing" element={<PricingContent />} />
                                  )}
                                  {!isPublicSector && (
                                    <Route path="accounting" element={<AccountingContent />} />
                                  )}

                                  {isMultiBookEnabled && (
                                    <Route path="tax" element={<TaxContent />} />
                                  )}

                                  <Route path="team" element={<TeamContent />} />
                                  {!isLegalEntityRootOrg && (
                                    <Route
                                      path="spam-tokens"
                                      element={<SpamTokenSettingContent />}
                                    />
                                  )}
                                  {isStableCoinsEnabled && (
                                    <Route path="stable-coins" element={<StableCoinsContent />} />
                                  )}
                                  {isAuditLogEnabled &&
                                    hasPermission(Permission.SettingAuditLogsRead) && (
                                      <Route path="audit-log" element={<AuditLogExport />} />
                                    )}
                                  {showCategoriesSettings && (
                                    <Route path="categories" element={<Categories />} />
                                  )}
                                  {user?.isSuperAdmin && (
                                    <>
                                      {!isZeusEnvironment() && (
                                        <>
                                          <Route
                                            path="org-controls"
                                            element={<OrgControlsContent />}
                                          />
                                          <Route path="requests" element={<RequestsContent />} />
                                        </>
                                      )}
                                      <Route path="entities" element={<EntitiesContent />} />
                                      <Route
                                        path="organization"
                                        element={<OrganizationContent />}
                                      />
                                      {enableCustomFields && (
                                        <Route path="custom-fields" element={<CustomFields />} />
                                      )}

                                      {!isZeusEnvironment() && (
                                        <>
                                          <Route
                                            path="imposter-allowlist"
                                            element={<ImposterAllowlistContent />}
                                          />
                                          <Route
                                            path="ingestion-jobs"
                                            element={<IngestionJobsContent />}
                                          />
                                        </>
                                      )}
                                    </>
                                  )}
                                  {user?.isZeusITAdmin && (
                                    <Route path="entities" element={<EntitiesContent />} />
                                  )}
                                </Route>
                              )
                            }
                            case BasePath.Contacts: {
                              return (
                                <Route element={<Contacts />} path={route.path} key={route.name}>
                                  <Route
                                    index={true}
                                    element={<Navigate to={ContactsTab.Saved} />}
                                  />
                                  <Route
                                    path={ContactsTab.Saved}
                                    element={<SavedContactsContent />}
                                  />
                                  <Route
                                    path={ContactsTab.UnknownAddresses}
                                    element={<UnknownAddressesContacts />}
                                  />
                                </Route>
                              )
                            }
                            case BasePath.VoucherDetails: {
                              return (
                                <Route
                                  element={<VoucherDetails />}
                                  path={route.path}
                                  key={route.name}
                                >
                                  <Route
                                    index={true}
                                    element={<Navigate to={VoucherDetailsTab.Overview} />}
                                  />
                                  <Route
                                    path={VoucherDetailsTab.Overview}
                                    element={<VoucherOverview />}
                                  />
                                  <Route
                                    path={VoucherDetailsTab.Transactions}
                                    element={<VoucherTransactions />}
                                  />
                                </Route>
                              )
                            }
                            case BasePath.Accounting: {
                              return (
                                <Route
                                  element={<AccountingRouteGuard />}
                                  path={route.path}
                                  key={route.name}
                                >
                                  <Route element={<AccountingCosmicContent />}>
                                    <Route
                                      index={true}
                                      element={<Navigate to={AccountingTab.ChartOfAccounts} />}
                                    />
                                    <Route
                                      path={AccountingTab.ChartOfAccounts}
                                      element={<ChartOfAccounts />}
                                    />
                                    <Route
                                      path={AccountingTab.DetailedMappings}
                                      element={<DetailedMappings />}
                                    />
                                  </Route>
                                </Route>
                              )
                            }
                            case BasePath.AccountingMapping: {
                              return (
                                <Route
                                  element={<AccountingRouteGuard />}
                                  path={route.path}
                                  key={route.name}
                                >
                                  <Route index={true} element={route.component} />
                                </Route>
                              )
                            }
                            // No default
                          }

                          return (
                            <Route path={route.path} key={route.name} element={route.component} />
                          )
                        })}
                        <Route path="*" element={<Navigate to={defaultLandingPath} />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </CosmicErrorBoundary>
              </Layout>
            </BackendFiltersProvider>
          </ReportFilterProvider>
        </FiltersProvider>
      </ConfirmProvider>
    </LegalEntityProvider>
  )
}

export default AuthRequiredContent
